import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook that provides debounced search state.
 *
 * Returns the immediate search value, the debounced value, and a setter.
 * The debounced value updates after the specified delay (default 300ms).
 */
export function useDebouncedSearch(delay = 300) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setDebouncedSearch(value);
      }, delay);
    },
    [delay],
  );

  return { search, debouncedSearch, handleSearchChange } as const;
}
